import { useQueryClient } from "@tanstack/react-query";
import { Building } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { Label } from "@krea/shared/design-system/Label";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { queryKeyMap } from "@/features/query-client";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";
import { LenderProfileSectionCard } from "./lender-profile-section-card";

const schema = yup.object().shape({
  name: yup.string().required("Company name is required"),
  customerContactPhoneNumber: yup.string().nonNullable(),
  customerContactEmail: yup.string().email("Invalid email").nonNullable(),
});

type IFormValues = yup.InferType<typeof schema>;

export const LenderProfileDetailsSection = () => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const queryClient = useQueryClient();

  const form = useForm<IFormValues>({
    mode: "onChange",
    defaultValues: {
      name: lenderCompany?.name ?? "",
      customerContactPhoneNumber:
        lenderCompany?.customerContactPhoneNumber ?? "",
      customerContactEmail: lenderCompany?.customerContactEmail ?? "",
    },
  });
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  useEffect(() => {
    if (lenderCompany) {
      const initialValues: IFormValues = {
        name: lenderCompany.name,
        customerContactPhoneNumber:
          lenderCompany.customerContactPhoneNumber ?? "",
        customerContactEmail: lenderCompany.customerContactEmail ?? "",
      };
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: (data) => {
            queryClient.setQueryData(
              queryKeyMap.getLenderCompany(lenderCompany.id),
              data,
            );
            toast.success("Company profile updated successfully");
          },
          onError: () => {
            toast.error("Failed to update company profile");
          },
        },
      );
    })();
  }, [lenderCompany, form, updateLenderCompany, queryClient]);

  return (
    <LenderProfileSectionCard
      title="Company Overview"
      description="Basic information about the lending company"
    >
      <div className="flex flex-col items-start gap-8 md:flex-row">
        <div className="flex flex-col items-center gap-3">
          {lenderCompany?.imageUrl ? (
            <img
              src={lenderCompany.imageUrl}
              alt="Company Logo"
              className="h-32 w-32 rounded-xl object-contain"
            />
          ) : (
            <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-primary ring-4 ring-gray-100">
              <Building className="h-10 w-10 text-white" />
            </div>
          )}
          <ResponsiveTooltip
            trigger={
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-primary"
              >
                Change Company Logo
              </Button>
            }
            interactive
          >
            To change the company logo, please contact customer support.
          </ResponsiveTooltip>
        </div>
        <Form {...form}>
          <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-5">
            <div className="col-span-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field, formState: { isSubmitting } }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Company Name"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      The name of the company that will be displayed to the
                      customers.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label>Description</Label>
                <div
                  className="rounded-md border border-gray-200 px-4 py-2 text-base font-medium text-black-500 shadow-md"
                  dangerouslySetInnerHTML={{
                    __html: lenderCompany?.description ?? "",
                  }}
                />
                <p className="text-sm text-gray-600">
                  The description of the company that will be displayed to the
                  customers. Contact customer support to update this
                  information.
                </p>
              </div>
            </div>

            <div className="col-span-2">
              <FormField
                control={form.control}
                name="customerContactPhoneNumber"
                render={({ field, formState: { isSubmitting } }) => (
                  <FormItem>
                    <FormLabel>Contact Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Contact Phone Number"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      The phone number of the company that will be displayed to
                      the customers when they contact the company after
                      selecting the lender&apos;s bid.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="col-span-2">
              <FormField
                control={form.control}
                name="customerContactEmail"
                render={({ field, formState: { isSubmitting } }) => (
                  <FormItem>
                    <FormLabel>Contact Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Contact Email"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      The email of the company that will be displayed to the
                      customers when they contact the company after selecting
                      the lender&apos;s bid.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            <div className="col-span-2">
              <Separator />
            </div>
            <div className="col-span-2 flex justify-end">
              <Button
                disabled={
                  form.formState.isSubmitting ||
                  !form.formState.isValid ||
                  !form.formState.isDirty
                }
                size="lg"
                onClick={handleSubmit}
              >
                Save
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </LenderProfileSectionCard>
  );
};
